import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, UserCheck, UserX, Shield, Building, 
  CheckCircle, XCircle, Clock, Search, Filter,
  Mail, Phone, MapPin, Calendar, Award, Eye, EyeOff,
  Edit, Trash2, UserPlus, FileText
} from 'lucide-react';
import SystemAdminLayout from '../layouts/SystemAdminLayout';

const UserManagement = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [organizationTypes, setOrganizationTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('organizations'); // organizations, evaluators, all_users, questions
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRole, setFilterRole] = useState('all');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({
    username: '',
    password: '',
    email: '',
    name: '',
    status: 'active'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showOrgModal, setShowOrgModal] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [showOrgPassword, setShowOrgPassword] = useState(false);
  const [orgForm, setOrgForm] = useState({
    name: '',
    email: '',
    organization_size: 'MEDIUM',
    industry: '',
    status: 'pending',
    username: '',
    new_password: ''
  });
  
  // Questions management state
  const [questions, setQuestions] = useState([]);
  const [showQuestionModal, setShowQuestionModal] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [questionForm, setQuestionForm] = useState({
    question_text: '',
    why_it_matters: '',
    metric: '',
    applicable_to: []
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchData();
    fetchIndustries();
    fetchOrganizationTypes();
    fetchQuestions();
  }, []);

  const fetchOrganizationTypes = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/organization-types`);
      if (response.ok) {
        const data = await response.json();
        setOrganizationTypes(data.organization_types || []);
      }
    } catch (error) {
      console.error('Error fetching organization types:', error);
    }
  };

  const fetchQuestions = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/questions`);
      if (response.ok) {
        const data = await response.json();
        setQuestions(data.questions || []);
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
    }
  };

  const fetchIndustries = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/industries`);
      if (response.ok) {
        const data = await response.json();
        setIndustries(Array.isArray(data) ? data : (data.industries || []));
      }
    } catch (error) {
      console.error('Error fetching industries:', error);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch organizations
      const orgsResponse = await fetch(`${BACKEND_URL}/api/admin/organizations`);
      const orgsData = await orgsResponse.json();
      setOrganizations(orgsData);

      // Fetch users
      const usersResponse = await fetch(`${BACKEND_URL}/api/admin/users`);
      const usersData = await usersResponse.json();
      setUsers(usersData);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditForm({
      username: user.username,
      password: '', // Don't pre-fill password
      email: user.email,
      name: user.name,
      status: user.status || 'active'
    });
    setShowEditModal(true);
  };

  const handleSaveUser = async () => {
    try {
      // Only send password if it was changed
      const updateData = {
        username: editForm.username,
        email: editForm.email,
        name: editForm.name,
        status: editForm.status
      };
      
      if (editForm.password && editForm.password.trim() !== '') {
        updateData.password = editForm.password;
      }

      const response = await fetch(`${BACKEND_URL}/api/admin/users/${editingUser.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updateData)
      });

      if (response.ok) {
        alert('User updated successfully!');
        setShowEditModal(false);
        fetchData(); // Refresh user list
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail || 'Failed to update user'}`);
      }
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Failed to update user');
    }
  };

  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approveOrgId, setApproveOrgId] = useState(null);
  const [approveForm, setApproveForm] = useState({ username: '', password: '' });
  const [showApprovePassword, setShowApprovePassword] = useState(false);

  const handleApproveOrganization = async (orgId) => {
    // Find the organization to get email
    const org = organizations.find(o => o.id === orgId);
    setApproveOrgId(orgId);
    setApproveForm({ username: org?.email || '', password: '' });
    setShowApproveModal(true);
  };

  const submitApproval = async () => {
    if (!approveForm.username) {
      alert('Please enter a username');
      return;
    }
    if (!approveForm.password || approveForm.password.length < 8) {
      alert('Password must be at least 8 characters');
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/organizations/${approveOrgId}/approve`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: approveForm.username, password: approveForm.password })
      });
      
      if (response.ok) {
        alert(`✅ Organization Approved!\n\n📧 Login Credentials:\n━━━━━━━━━━━━━━━━━━━━\nUsername: ${approveForm.username}\nPassword: ${approveForm.password}\n━━━━━━━━━━━━━━━━━━━━\n\n⚠️ Please save and share these credentials with the organization!`);
        setShowApproveModal(false);
        fetchData();
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail || 'Failed to approve'}`);
      }
    } catch (error) {
      console.error('Error approving organization:', error);
      alert('Error approving organization');
    }
  };

  const handleRejectOrganization = async (orgId) => {
    if (!window.confirm('Are you sure you want to reject this organization?')) return;
    
    try {
      await fetch(`${BACKEND_URL}/api/admin/organizations/${orgId}/reject`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' }
      });
      fetchData();
    } catch (error) {
      console.error('Error rejecting organization:', error);
    }
  };

  const handleEditOrganization = async (org) => {
    // Find the org admin user
    const user = users.find(u => u.organization_id === org.id && u.role === 'org_admin');
    
    setEditingOrg(org);
    setOrgForm({
      name: org.name || '',
      email: org.email || '',
      organization_size: org.organization_size || 'MEDIUM',
      industry: org.industry || '',
      status: org.status || 'pending',
      username: user?.username || user?.email || org.email || '',
      new_password: ''
    });
    setShowOrgModal(true);
  };

  const handleAddOrganization = () => {
    setEditingOrg(null);
    setOrgForm({
      name: '',
      email: '',
      organization_size: 'MEDIUM',
      industry: '',
      status: 'pending',
      username: '',
      new_password: ''
    });
    setShowOrgModal(true);
  };

  const handleSaveOrganization = async () => {
    try {
      if (editingOrg) {
        // Update existing organization - include credentials in same request
        const orgData = {
          name: orgForm.name,
          email: orgForm.email,
          organization_size: orgForm.organization_size,
          industry: orgForm.industry,
          status: orgForm.status
        };
        
        // CRITICAL: Include username and password in the same request
        // The backend update_organization endpoint handles syncing to users collection
        if (orgForm.username) {
          orgData.username = orgForm.username;
        }
        if (orgForm.new_password && orgForm.new_password.trim() !== '') {
          orgData.password = orgForm.new_password;
        }
        
        const response = await fetch(`${BACKEND_URL}/api/admin/organizations/${editingOrg.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(orgData)
        });
        
        const result = await response.json();
        
        if (!response.ok) {
          alert(`Error: ${result.detail || 'Failed to update organization'}`);
          return;
        }
        
        let message = 'Organization updated successfully!';
        if (result.credentials_updated) {
          message += `\n\n✅ Updated Login Credentials:`;
          if (result.username) message += `\nUsername: ${result.username}`;
          if (result.password_updated) message += `\nNew Password: ${orgForm.new_password}`;
        }
        alert(message);
        setShowOrgModal(false);
        fetchData();
      } else {
        // Create new organization - username will be email by default
        const createData = {
          name: orgForm.name,
          email: orgForm.email,
          organization_size: orgForm.organization_size,
          industry: orgForm.industry,
          status: orgForm.status,
          username: orgForm.username || orgForm.email,
          password: orgForm.new_password || 'TempPass123!'
        };
        
        const response = await fetch(`${BACKEND_URL}/api/admin/organizations`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(createData)
        });
        
        if (response.ok) {
          const result = await response.json();
          alert(`✅ Organization created!\n\n📧 Login Credentials:\nUsername: ${result.username || createData.username}\nPassword: ${result.password || createData.password}\n\n⚠️ Please save these credentials!`);
          setShowOrgModal(false);
          fetchData();
        } else {
          const error = await response.json();
          alert(`Error: ${error.detail || 'Failed to create organization'}`);
        }
      }
    } catch (error) {
      console.error('Error saving organization:', error);
      alert('Failed to save organization');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await fetch(`${BACKEND_URL}/api/admin/users/${userId}`, {
        method: 'DELETE'
      });
      fetchData();
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  // Question editing functions
  const handleEditQuestion = (question) => {
    setEditingQuestion(question);
    setQuestionForm({
      question_text: question.question_text || '',
      why_it_matters: question.why_it_matters || '',
      metric: question.metric || '',
      applicable_to: question.applicable_to || []
    });
    setShowQuestionModal(true);
  };

  const handleSaveQuestion = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/questions/${editingQuestion.question_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(questionForm)
      });
      
      if (response.ok) {
        alert('Question updated successfully!');
        setShowQuestionModal(false);
        fetchQuestions();
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail || 'Failed to update question'}`);
      }
    } catch (error) {
      console.error('Error saving question:', error);
      alert('Failed to save question');
    }
  };

  const toggleQuestionApplicability = (orgType) => {
    setQuestionForm(prev => ({
      ...prev,
      applicable_to: prev.applicable_to.includes(orgType)
        ? prev.applicable_to.filter(t => t !== orgType)
        : [...prev.applicable_to, orgType]
    }));
  };

  const getStatusBadge = (status) => {
    const badges = {
      approved: { color: 'bg-green-100 text-green-800 border-green-300', label: 'Approved', icon: CheckCircle },
      pending: { color: 'bg-yellow-100 text-yellow-800 border-yellow-300', label: 'Pending', icon: Clock },
      rejected: { color: 'bg-red-100 text-red-800 border-red-300', label: 'Rejected', icon: XCircle },
      active: { color: 'bg-green-100 text-green-800 border-green-300', label: 'Active', icon: CheckCircle }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold border-2 ${badge.color}`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  const getRoleBadge = (role) => {
    const badges = {
      super_admin: { color: 'bg-red-100 text-red-800 border-red-400', label: 'Super Admin' },
      org_admin: { color: 'bg-blue-100 text-blue-800 border-blue-400', label: 'Org Admin' },
      evaluator: { color: 'bg-purple-100 text-purple-800 border-purple-400', label: 'Evaluator' },
      explorer: { color: 'bg-teal-100 text-teal-800 border-teal-400', label: 'Explorer' },
      mentor: { color: 'bg-orange-100 text-orange-800 border-orange-400', label: 'Mentor' },
      partner: { color: 'bg-indigo-100 text-indigo-800 border-indigo-400', label: 'Partner' }
    };
    const badge = badges[role] || badges.org_admin;
    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-semibold border ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  const getSizeBadge = (size) => {
    const badges = {
      Micro: 'bg-gray-100 text-gray-700',
      Small: 'bg-blue-100 text-blue-700',
      Medium: 'bg-indigo-100 text-indigo-700',
      Large: 'bg-purple-100 text-purple-700',
      Enterprise: 'bg-pink-100 text-pink-700'
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-semibold ${badges[size] || badges.Medium}`}>
        {size}
      </span>
    );
  };

  const getTierBadge = (tier) => {
    if (!tier) return null;
    const badges = {
      Luminary: { color: 'bg-yellow-100 text-yellow-800 border-yellow-500', icon: '🏆' },
      Inspired: { color: 'bg-gray-100 text-gray-800 border-gray-500', icon: '⚡' },
      Aspiring: { color: 'bg-orange-100 text-orange-800 border-orange-500', icon: '🎯' }
    };
    const badge = badges[tier];
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold border-2 ${badge.color}`}>
        <span>{badge.icon}</span>
        {tier}
      </span>
    );
  };

  // Filter and search
  const filteredOrganizations = organizations.filter(org => {
    const matchesSearch = org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         org.industry.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || org.status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  const filteredUsers = users.filter(user => {
    const matchesSearch = user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = filterRole === 'all' || user.role === filterRole;
    return matchesSearch && matchesRole;
  });

  const evaluators = users.filter(u => u.role === 'evaluator');
  const orgAdmins = users.filter(u => u.role === 'org_admin');
  const mentors = users.filter(u => u.role === 'mentor');
  const partners = users.filter(u => u.role === 'partner');

  const stats = {
    total_orgs: organizations.length,
    pending_orgs: organizations.filter(o => o.status === 'pending').length,
    approved_orgs: organizations.filter(o => o.status === 'approved').length,
    total_evaluators: evaluators.length,
    total_org_admins: orgAdmins.length,
    total_mentors: mentors.length,
    total_partners: partners.length,
    certified_orgs: organizations.filter(o => o.certification_level).length
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading user management...</p>
        </div>
      </div>
    );
  }

  return (
    <SystemAdminLayout>
    <div className="bg-gray-50 min-h-full">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-8 px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-black mb-2">User Management</h1>
              <p className="text-blue-100">Manage organizations, users, and evaluator assignments</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-8">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center gap-3">
              <Building className="w-8 h-8 text-blue-600" />
              <div>
                <p className="text-xs text-gray-600">Total Orgs</p>
                <p className="text-2xl font-black text-gray-900">{stats.total_orgs}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-yellow-600" />
              <div>
                <p className="text-xs text-gray-600">Pending</p>
                <p className="text-2xl font-black text-yellow-600">{stats.pending_orgs}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-green-600" />
              <div>
                <p className="text-xs text-gray-600">Approved</p>
                <p className="text-2xl font-black text-green-600">{stats.approved_orgs}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center gap-3">
              <Award className="w-8 h-8 text-purple-600" />
              <div>
                <p className="text-xs text-gray-600">Certified</p>
                <p className="text-2xl font-black text-purple-600">{stats.certified_orgs}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center gap-3">
              <UserCheck className="w-8 h-8 text-orange-600" />
              <div>
                <p className="text-xs text-gray-600">Evaluators</p>
                <p className="text-2xl font-black text-orange-600">{stats.total_evaluators}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-indigo-600" />
              <div>
                <p className="text-xs text-gray-600">Org Admins</p>
                <p className="text-2xl font-black text-indigo-600">{stats.total_org_admins}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-xl overflow-hidden">
          <div className="border-b border-gray-200">
            <div className="flex">
              <button
                onClick={() => setActiveTab('organizations')}
                className={`flex-1 px-6 py-4 font-bold text-sm transition-all ${
                  activeTab === 'organizations'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Building className="w-5 h-5 inline mr-2" />
                Organizations ({filteredOrganizations.length})
              </button>
              <button
                onClick={() => setActiveTab('evaluators')}
                className={`flex-1 px-6 py-4 font-bold text-sm transition-all ${
                  activeTab === 'evaluators'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}
              >
                <UserCheck className="w-5 h-5 inline mr-2" />
                Evaluators ({evaluators.length})
              </button>
              <button
                onClick={() => setActiveTab('mentors')}
                className={`flex-1 px-6 py-4 font-bold text-sm transition-all ${
                  activeTab === 'mentors'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Award className="w-5 h-5 inline mr-2" />
                Mentors ({mentors.length})
              </button>
              <button
                onClick={() => setActiveTab('partners')}
                className={`flex-1 px-6 py-4 font-bold text-sm transition-all ${
                  activeTab === 'partners'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Shield className="w-5 h-5 inline mr-2" />
                Partners ({partners.length})
              </button>
              <button
                onClick={() => setActiveTab('all_users')}
                className={`flex-1 px-6 py-4 font-bold text-sm transition-all ${
                  activeTab === 'all_users'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Users className="w-5 h-5 inline mr-2" />
                All Users ({filteredUsers.length})
              </button>
              <button
                onClick={() => setActiveTab('questions')}
                className={`flex-1 px-6 py-4 font-bold text-sm transition-all ${
                  activeTab === 'questions'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                }`}
              >
                <FileText className="w-5 h-5 inline mr-2" />
                Questions ({questions.length})
              </button>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="p-6 bg-gray-50 border-b border-gray-200">
            <div className="flex gap-4 items-center">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                />
              </div>
              {activeTab === 'organizations' && (
                <>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none font-semibold"
                  >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                  <button
                    onClick={handleAddOrganization}
                    className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition-all"
                  >
                    <UserPlus className="w-5 h-5" />
                    Add Organization
                  </button>
                </>
              )}
              {activeTab === 'all_users' && (
                <select
                  value={filterRole}
                  onChange={(e) => setFilterRole(e.target.value)}
                  className="px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none font-semibold"
                >
                  <option value="all">All Roles</option>
                  <option value="org_admin">Org Admin</option>
                  <option value="evaluator">Evaluator</option>
                  <option value="mentor">Mentor</option>
                  <option value="partner">Partner</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Organizations Tab */}
            {activeTab === 'organizations' && (
              <div className="space-y-4">
                {filteredOrganizations.length === 0 ? (
                  <div className="text-center py-12">
                    <Building className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-600">No organizations found</p>
                  </div>
                ) : (
                  filteredOrganizations.map((org) => (
                    <div
                      key={org.id}
                      className="border-2 border-gray-200 rounded-xl p-6 hover:border-blue-300 hover:shadow-lg transition-all bg-white"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2 flex-wrap">
                            <h3 className="text-xl font-bold text-gray-900">{org.name}</h3>
                            {getStatusBadge(org.status)}
                            {org.certification_tier && getTierBadge(org.certification_tier)}
                            {getSizeBadge(org.organization_size)}
                          </div>
                          <p className="text-sm text-gray-600 mb-3">{org.industry || 'Business'}</p>
                          <div className="grid grid-cols-2 gap-4 text-sm">
                            <div className="flex items-center gap-2 text-gray-700">
                              <Building className="w-4 h-4" />
                              <span>{org.organization_size || 'N/A'}</span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-700">
                              <Mail className="w-4 h-4" />
                              <span>{org.email || 'N/A'}</span>
                            </div>
                            <div className="flex items-center gap-2 text-gray-700">
                              <Calendar className="w-4 h-4" />
                              <span>Registered: {org.created_at ? new Date(org.created_at).toLocaleDateString() : 'N/A'}</span>
                            </div>
                            {org.overall_score > 0 && (
                              <div className="flex items-center gap-2 text-gray-700">
                                <Award className="w-4 h-4" />
                                <span>Score: {org.overall_score}/100</span>
                              </div>
                            )}
                          </div>
                          {org.overall_score > 0 && (
                            <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                              <p className="text-sm font-semibold text-blue-900">
                                Certification Score: <span className="text-2xl">{org.overall_score}/100</span>
                              </p>
                            </div>
                          )}
                        </div>

                        <div className="flex flex-col gap-2 ml-6">
                          {org.status === 'pending' && (
                            <>
                              <button
                                onClick={() => handleApproveOrganization(org.id)}
                                className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition-all"
                              >
                                <CheckCircle className="w-4 h-4" />
                                Approve
                              </button>
                              <button
                                onClick={() => handleRejectOrganization(org.id)}
                                className="flex items-center gap-2 px-6 py-2 bg-red-600 text-white rounded-lg font-bold hover:bg-red-700 transition-all"
                              >
                                <XCircle className="w-4 h-4" />
                                Reject
                              </button>
                            </>
                          )}
                          {/* Edit button - always visible */}
                          <button
                            onClick={() => handleEditOrganization(org)}
                            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg font-bold hover:bg-indigo-700 transition-all"
                            title="Edit Organization"
                          >
                            <Edit className="w-4 h-4" />
                            Edit
                          </button>
                          <button
                            onClick={() => window.location.href = `/admin/org-details/${org.id}`}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition-all"
                            title="View Organization Details"
                          >
                            <Eye className="w-4 h-4" />
                            View
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Evaluators Tab */}
            {activeTab === 'evaluators' && (
              <div className="space-y-4">
                <div className="flex justify-end mb-4">
                  <button 
                    onClick={() => navigate('/admin/evaluator-management')}
                    className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-bold hover:from-blue-700 hover:to-indigo-700 transition-all"
                    data-testid="add-evaluator-btn"
                  >
                    <UserPlus className="w-5 h-5" />
                    Add New Evaluator
                  </button>
                </div>
                {evaluators.length === 0 ? (
                  <div className="text-center py-12">
                    <UserCheck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-600">No evaluators found</p>
                  </div>
                ) : (
                  evaluators.map((user) => (
                    <div
                      key={user.id}
                      className="border-2 border-gray-200 rounded-xl p-6 hover:border-purple-300 hover:shadow-lg transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                            <UserCheck className="w-6 h-6 text-purple-600" />
                          </div>
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">{user.name}</h3>
                            <p className="text-sm text-gray-600">{user.email}</p>
                            <div className="flex items-center gap-2 mt-1">
                              {getRoleBadge(user.role)}
                              {getStatusBadge(user.status)}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEditUser(user)}
                            className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-all"
                          >
                            <Edit className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user.id)}
                            className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-all"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Mentors Tab */}
            {activeTab === 'mentors' && (
              <div className="space-y-4">
                <div className="flex justify-end mb-4">
                  <button className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-bold hover:from-orange-600 hover:to-amber-600 transition-all">
                    <UserPlus className="w-5 h-5" />
                    Add New Mentor
                  </button>
                </div>
                {mentors.length === 0 ? (
                  <div className="text-center py-12">
                    <Award className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-600">No mentors found</p>
                  </div>
                ) : (
                  mentors.map((user) => (
                    <div
                      key={user.id}
                      className="border-2 border-gray-200 rounded-xl p-6 hover:border-orange-300 hover:shadow-lg transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
                            <Award className="w-6 h-6 text-orange-600" />
                          </div>
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">{user.full_name || user.name || user.username}</h3>
                            <p className="text-sm text-gray-600">{user.email}</p>
                            <p className="text-xs text-gray-500">Username: {user.username}</p>
                            {user.title && <p className="text-xs text-orange-600 font-medium">{user.title}</p>}
                            <div className="flex items-center gap-2 mt-1">
                              {getRoleBadge(user.role)}
                              {getStatusBadge(user.status)}
                            </div>
                            {user.expertise_areas && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {user.expertise_areas.map((exp, i) => (
                                  <span key={i} className="bg-orange-50 text-orange-700 px-2 py-0.5 rounded text-xs">
                                    {exp}
                                  </span>
                                ))}
                              </div>
                            )}
                            {user.assigned_organizations && user.assigned_organizations.length > 0 && (
                              <p className="text-xs text-gray-500 mt-1">
                                Assigned to {user.assigned_organizations.length} organizations
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          {user.rating && (
                            <div className="flex items-center gap-1 text-amber-500">
                              <span className="text-sm font-bold">{user.rating}</span>
                              <span className="text-xs">★</span>
                            </div>
                          )}
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEditUser(user)}
                              className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-all"
                            >
                              <Edit className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDeleteUser(user.id)}
                              className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-all"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Partners Tab */}
            {activeTab === 'partners' && (
              <div className="space-y-4">
                <div className="flex justify-end mb-4">
                  <button className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-lg font-bold hover:from-indigo-600 hover:to-purple-600 transition-all">
                    <UserPlus className="w-5 h-5" />
                    Add New Partner
                  </button>
                </div>
                {partners.length === 0 ? (
                  <div className="text-center py-12">
                    <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-600">No partners found</p>
                  </div>
                ) : (
                  partners.map((user) => (
                    <div
                      key={user.id}
                      className="border-2 border-gray-200 rounded-xl p-6 hover:border-indigo-300 hover:shadow-lg transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                            <Shield className="w-6 h-6 text-indigo-600" />
                          </div>
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">{user.full_name || user.company_name || user.name || user.username}</h3>
                            {user.company_name && user.full_name !== user.company_name && (
                              <p className="text-sm text-indigo-600 font-medium">{user.company_name}</p>
                            )}
                            <p className="text-sm text-gray-600">{user.email}</p>
                            <p className="text-xs text-gray-500">Username: {user.username}</p>
                            {user.title && <p className="text-xs text-indigo-600 font-medium">{user.title}</p>}
                            <div className="flex items-center gap-2 mt-1">
                              {getRoleBadge(user.role)}
                              {getStatusBadge(user.status)}
                            </div>
                            {user.services && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {user.services.map((svc, i) => (
                                  <span key={i} className="bg-purple-50 text-purple-700 px-2 py-0.5 rounded text-xs">
                                    {svc}
                                  </span>
                                ))}
                              </div>
                            )}
                            {user.total_projects && (
                              <p className="text-xs text-gray-500 mt-1">
                                {user.total_projects} projects | {user.clients_served} clients served
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          {user.rating && (
                            <div className="flex items-center gap-1 text-amber-500">
                              <span className="text-sm font-bold">{user.rating}</span>
                              <span className="text-xs">★</span>
                            </div>
                          )}
                          {user.revenue_ytd && (
                            <p className="text-sm font-bold text-green-600">
                              ${user.revenue_ytd.toLocaleString()} YTD
                            </p>
                          )}
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEditUser(user)}
                              className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-all"
                            >
                              <Edit className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDeleteUser(user.id)}
                              className="p-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-all"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* All Users Tab */}
            {activeTab === 'all_users' && (
              <div className="space-y-4">
                {filteredUsers.length === 0 ? (
                  <div className="text-center py-12">
                    <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-600">No users found</p>
                  </div>
                ) : (
                  filteredUsers.map((user) => (
                    <div
                      key={user.id}
                      className="border-2 border-gray-200 rounded-xl p-6 hover:border-indigo-300 hover:shadow-lg transition-all"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                            <Shield className="w-6 h-6 text-indigo-600" />
                          </div>
                          <div>
                            <h3 className="text-lg font-bold text-gray-900">{user.name}</h3>
                            <p className="text-sm text-gray-600">{user.email}</p>
                            <div className="flex items-center gap-2 mt-1">
                              {getRoleBadge(user.role)}
                              {getStatusBadge(user.status)}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEditUser(user)}
                            className="p-2 bg-green-100 text-green-600 rounded-lg hover:bg-green-200 transition-all"
                            title="Edit User"
                          >
                            <Edit className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => alert(`View user ${user.name}`)}
                            className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-all"
                            title="View Details"
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Questions Tab */}
            {activeTab === 'questions' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Assessment Questions</h3>
                    <p className="text-sm text-gray-600">Manage question applicability by organization type</p>
                  </div>
                </div>
                
                {questions.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-600">No questions found</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {questions
                      .filter(q => 
                        q.question_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        q.question_text?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        q.pillar_name?.toLowerCase().includes(searchTerm.toLowerCase())
                      )
                      .map((question) => (
                      <div
                        key={question.question_id}
                        className="bg-white rounded-xl border-2 border-gray-200 hover:border-purple-300 p-4 transition-all"
                      >
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-bold">
                                {question.question_id}
                              </span>
                              <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                                question.question_type === 'qualitative' 
                                  ? 'bg-blue-100 text-blue-700' 
                                  : 'bg-green-100 text-green-700'
                              }`}>
                                {question.question_type}
                              </span>
                              <span className="text-xs text-gray-500">
                                Pillar {question.pillar_number}: {question.pillar_name}
                              </span>
                            </div>
                            <p className="text-gray-900 font-medium mb-2">{question.question_text}</p>
                            <div className="flex flex-wrap gap-1">
                              {['MICRO', 'SMALL', 'MEDIUM', 'LARGE', 'ENTERPRISE'].map((orgType) => (
                                <span
                                  key={orgType}
                                  className={`px-2 py-0.5 rounded text-xs font-medium ${
                                    question.applicable_to?.includes(orgType)
                                      ? 'bg-green-100 text-green-700 border border-green-300'
                                      : 'bg-gray-100 text-gray-400 border border-gray-200'
                                  }`}
                                >
                                  {orgType}
                                </span>
                              ))}
                            </div>
                          </div>
                          <button
                            onClick={() => handleEditQuestion(question)}
                            className="p-2 bg-purple-100 text-purple-600 rounded-lg hover:bg-purple-200 transition-all ml-4"
                            title="Edit Question"
                          >
                            <Edit className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit User Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl">
            <div className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white p-6 rounded-t-2xl">
              <h2 className="text-2xl font-bold">Edit User</h2>
              <p className="text-blue-100 mt-1">Update user credentials and information</p>
            </div>
            
            <div className="p-8 space-y-6">
              {/* Username */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Username
                </label>
                <input
                  type="text"
                  value={editForm.username}
                  onChange={(e) => setEditForm({...editForm, username: e.target.value})}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-indigo-500 focus:outline-none"
                  placeholder="Enter username"
                />
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Password <span className="text-gray-500 font-normal">(Leave blank to keep current)</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={editForm.password}
                    onChange={(e) => setEditForm({...editForm, password: e.target.value})}
                    className="w-full px-4 py-3 pr-12 border-2 border-gray-300 rounded-lg focus:border-indigo-500 focus:outline-none"
                    placeholder="Enter new password (min 8 characters)"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-indigo-600 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Name
                </label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-indigo-500 focus:outline-none"
                  placeholder="Enter full name"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email
                </label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-indigo-500 focus:outline-none"
                  placeholder="Enter email address"
                />
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Status
                </label>
                <select
                  value={editForm.status}
                  onChange={(e) => setEditForm({...editForm, status: e.target.value})}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-indigo-500 focus:outline-none"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 pt-4">
                <button
                  onClick={handleSaveUser}
                  className="flex-1 bg-gradient-to-r from-indigo-600 to-blue-600 text-white px-6 py-3 rounded-lg font-bold hover:shadow-lg transition-all"
                >
                  Save Changes
                </button>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-bold hover:bg-gray-300 transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Organization Edit/Add Modal */}
      {showOrgModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-green-600 to-teal-600 text-white p-6 rounded-t-2xl flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold">{editingOrg ? 'Edit Organization' : 'Add New Organization'}</h2>
                  <p className="text-green-100 mt-1">{editingOrg ? 'Update organization details' : 'Create a new organization'}</p>
                </div>
                <button
                  onClick={() => setShowOrgModal(false)}
                  className="text-white hover:bg-white/20 p-2 rounded-lg transition-all"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            {/* Modal Body - Scrollable */}
            <div className="p-6 space-y-5 overflow-y-auto flex-1">
              {/* Organization Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Organization Name *
                </label>
                <input
                  type="text"
                  value={orgForm.name}
                  onChange={(e) => setOrgForm({...orgForm, name: e.target.value})}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-green-500 focus:outline-none"
                  placeholder="Enter organization name"
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Contact Email *
                </label>
                <input
                  type="email"
                  value={orgForm.email}
                  onChange={(e) => setOrgForm({...orgForm, email: e.target.value})}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-green-500 focus:outline-none"
                  placeholder="admin@organization.com"
                />
              </div>

              {/* Company Size & Industry Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Company Size *
                  </label>
                  <select
                    value={orgForm.organization_size}
                    onChange={(e) => setOrgForm({...orgForm, organization_size: e.target.value})}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-green-500 focus:outline-none"
                  >
                    <option value="MICRO">MICRO - $1,200</option>
                    <option value="SMALL">SMALL - $1,800</option>
                    <option value="MEDIUM">MEDIUM - $2,500</option>
                    <option value="LARGE">LARGE - $7,500</option>
                    <option value="ENTERPRISE">ENTERPRISE - $15,000</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Status
                  </label>
                  <select
                    value={orgForm.status}
                    onChange={(e) => setOrgForm({...orgForm, status: e.target.value})}
                    className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-green-500 focus:outline-none"
                  >
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
              </div>

              {/* Industry */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Industry
                </label>
                <select
                  value={orgForm.industry}
                  onChange={(e) => setOrgForm({...orgForm, industry: e.target.value})}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-green-500 focus:outline-none"
                >
                  <option value="">Select Industry</option>
                  {industries.map((ind) => (
                    <option key={ind.id || ind.name} value={ind.name}>
                      {ind.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Login Credentials Section */}
              <div className="border-t border-gray-200 pt-4">
                <h3 className="text-md font-bold text-gray-800 mb-3">🔐 Login Credentials</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Username
                    </label>
                    <input
                      type="text"
                      value={orgForm.username}
                      onChange={(e) => setOrgForm({...orgForm, username: e.target.value})}
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-green-500 focus:outline-none"
                      placeholder="Use email as username"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      {editingOrg ? 'New Password' : 'Password *'}
                    </label>
                    <div className="relative">
                      <input
                        type={showOrgPassword ? "text" : "password"}
                        value={orgForm.new_password}
                        onChange={(e) => setOrgForm({...orgForm, new_password: e.target.value})}
                        className="w-full px-4 py-2 pr-10 border-2 border-gray-300 rounded-lg focus:border-green-500 focus:outline-none"
                        placeholder={editingOrg ? "Leave blank to keep" : "Min 8 characters"}
                      />
                      <button
                        type="button"
                        onClick={() => setShowOrgPassword(!showOrgPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                      >
                        {showOrgPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer - Fixed at bottom */}
            <div className="p-4 border-t border-gray-200 flex gap-3 flex-shrink-0 bg-gray-50 rounded-b-2xl">
              <button
                onClick={handleSaveOrganization}
                className="flex-1 bg-gradient-to-r from-green-600 to-teal-600 text-white px-6 py-3 rounded-lg font-bold hover:shadow-lg transition-all"
              >
                {editingOrg ? 'Save Changes' : 'Create Organization'}
              </button>
              <button
                onClick={() => setShowOrgModal(false)}
                className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-bold hover:bg-gray-300 transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Approve Organization Modal */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white p-6 rounded-t-2xl">
              <h2 className="text-2xl font-bold">Approve Organization</h2>
              <p className="text-green-100 mt-1">Create login credentials for the organization</p>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Username */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Username *
                </label>
                <input
                  type="text"
                  value={approveForm.username}
                  onChange={(e) => setApproveForm({...approveForm, username: e.target.value})}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-green-500 focus:outline-none"
                  placeholder="Enter username (suggested: use email)"
                />
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Password * <span className="text-gray-500 font-normal">(min 8 characters)</span>
                </label>
                <div className="relative">
                  <input
                    type={showApprovePassword ? "text" : "password"}
                    value={approveForm.password}
                    onChange={(e) => setApproveForm({...approveForm, password: e.target.value})}
                    className="w-full px-4 py-3 pr-12 border-2 border-gray-300 rounded-lg focus:border-green-500 focus:outline-none"
                    placeholder="Enter initial password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowApprovePassword(!showApprovePassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-green-600 transition-colors"
                  >
                    {showApprovePassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Info Box */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800">
                  <strong>📧 These credentials will be shared with the organization admin.</strong>
                  <br />
                  They can use these to login and access their True Profile dashboard.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <button
                  onClick={submitApproval}
                  className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3 rounded-lg font-bold hover:shadow-lg transition-all"
                >
                  ✅ Approve Organization
                </button>
                <button
                  onClick={() => setShowApproveModal(false)}
                  className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-bold hover:bg-gray-300 transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Approve Organization Modal */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full shadow-2xl">
            <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white p-6 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold">Approve Organization</h2>
                  <p className="text-green-100 mt-1">Create login credentials</p>
                </div>
                <button
                  onClick={() => setShowApproveModal(false)}
                  className="text-white hover:bg-white/20 p-2 rounded-lg transition-all"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6 space-y-4">
              {/* Username */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Username *
                </label>
                <input
                  type="text"
                  value={approveForm.username}
                  onChange={(e) => setApproveForm({...approveForm, username: e.target.value})}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-green-500 focus:outline-none"
                  placeholder="Enter username (suggested: use email)"
                />
              </div>

              {/* Password with Eye Icon */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Password * <span className="text-gray-500 font-normal">(min 8 characters)</span>
                </label>
                <div className="relative">
                  <input
                    type={showApprovePassword ? "text" : "password"}
                    value={approveForm.password}
                    onChange={(e) => setApproveForm({...approveForm, password: e.target.value})}
                    className="w-full px-4 py-3 pr-12 border-2 border-gray-300 rounded-lg focus:border-green-500 focus:outline-none"
                    placeholder="Enter initial password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowApprovePassword(!showApprovePassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-green-600 transition-colors"
                  >
                    {showApprovePassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Info Box */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-800">
                  <strong>📧 Share these credentials with the organization admin.</strong>
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={submitApproval}
                  className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 text-white px-6 py-3 rounded-lg font-bold hover:shadow-lg transition-all"
                >
                  ✅ Approve
                </button>
                <button
                  onClick={() => setShowApproveModal(false)}
                  className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-bold hover:bg-gray-300 transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Question Edit Modal */}
      {showQuestionModal && editingQuestion && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-3xl w-full shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-6 rounded-t-2xl">
              <h2 className="text-2xl font-bold">Edit Question</h2>
              <p className="text-purple-100 mt-1">{editingQuestion.question_id} - {editingQuestion.pillar_name}</p>
            </div>
            
            <div className="p-8 space-y-6">
              {/* Question Text */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Question Text
                </label>
                <textarea
                  value={questionForm.question_text}
                  onChange={(e) => setQuestionForm({...questionForm, question_text: e.target.value})}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none"
                  rows={3}
                  placeholder="Enter the question text"
                />
              </div>

              {/* Why It Matters */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Why It Matters
                </label>
                <textarea
                  value={questionForm.why_it_matters}
                  onChange={(e) => setQuestionForm({...questionForm, why_it_matters: e.target.value})}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none"
                  rows={2}
                  placeholder="Explain why this question matters for conscious business assessment"
                />
              </div>

              {/* Metric (for quantitative questions) */}
              {editingQuestion.question_type === 'quantitative' && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Metric / KPI Expected
                  </label>
                  <input
                    type="text"
                    value={questionForm.metric}
                    onChange={(e) => setQuestionForm({...questionForm, metric: e.target.value})}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none"
                    placeholder="e.g., Percentage of employees trained"
                  />
                </div>
              )}

              {/* Organization Type Applicability */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Applicable To Organization Types
                </label>
                <p className="text-sm text-gray-500 mb-3">
                  Select which organization sizes this question applies to:
                </p>
                <div className="grid grid-cols-5 gap-3">
                  {['MICRO', 'SMALL', 'MEDIUM', 'LARGE', 'ENTERPRISE'].map((orgType) => (
                    <button
                      key={orgType}
                      onClick={() => toggleQuestionApplicability(orgType)}
                      className={`p-4 rounded-xl border-2 font-bold text-sm transition-all ${
                        questionForm.applicable_to.includes(orgType)
                          ? 'bg-purple-100 border-purple-500 text-purple-700'
                          : 'bg-gray-50 border-gray-300 text-gray-500 hover:border-gray-400'
                      }`}
                    >
                      <div className="flex flex-col items-center gap-2">
                        <span className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                          questionForm.applicable_to.includes(orgType)
                            ? 'bg-purple-500 border-purple-500'
                            : 'bg-white border-gray-300'
                        }`}>
                          {questionForm.applicable_to.includes(orgType) && (
                            <CheckCircle className="w-4 h-4 text-white" />
                          )}
                        </span>
                        {orgType}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Info Box */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <p className="text-sm text-purple-800">
                  <strong>💡 Note:</strong> Questions selected for smaller organization types (Micro, Small) should be 
                  fundamental practices. Larger organizations (Large, Enterprise) can have more complex requirements.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4 pt-4">
                <button
                  onClick={handleSaveQuestion}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-6 py-3 rounded-lg font-bold hover:shadow-lg transition-all"
                >
                  Save Changes
                </button>
                <button
                  onClick={() => setShowQuestionModal(false)}
                  className="flex-1 bg-gray-200 text-gray-700 px-6 py-3 rounded-lg font-bold hover:bg-gray-300 transition-all"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </SystemAdminLayout>
  );
};

export default UserManagement;
